// The real calendar's two small translations (B1, B3): the live map's staff
// snapshot into the board's live states, and the hub list down to the hubs an
// operator works — the pilot hubs, plus any other hub with something on the
// board (the cloud holds ~20 QA test hubs). Pure, so tested.

import type { OperationalCalendarReadModels } from "@bednbody/types";
import type { CalendarHub, LiveState } from "./calendar-board";

/** The slice of a live-map snapshot row the calendar reads. */
export interface LiveStaffSnapshot {
  id: string;
  mapStatus: string | null;
  opsStatus: { activity: string; connected: boolean } | null;
}

/**
 * One live state per person, the loudest first: an open SOS, then a lost
 * signal, then a break, then waiting for a pickup. Anyone else is left out —
 * the board falls back to the plan for them.
 */
export function liveStatesFrom(
  staff: readonly LiveStaffSnapshot[],
  openSosStaffIds: ReadonlySet<string>,
): Map<string, LiveState> {
  const states = new Map<string, LiveState>();
  for (const id of openSosStaffIds) states.set(id, "sos");
  for (const member of staff) {
    if (states.has(member.id)) continue;
    const ops = member.opsStatus;
    if (ops?.connected === false || member.mapStatus === "offline") {
      states.set(member.id, "disconnected");
    } else if (ops?.activity === "on_break") {
      states.set(member.id, "on_break");
    } else if (ops?.activity === "waiting_for_pick_up") {
      states.set(member.id, "waiting_pickup");
    }
  }
  return states;
}

/** The pilot hubs (the live map's set), plus any hub with an entry, a booking or a trip in range. */
export function calendarHubs(
  hubs: readonly (CalendarHub & { isPilot: boolean })[],
  models: OperationalCalendarReadModels,
): CalendarHub[] {
  const used = new Set([
    ...models.staff.map((entry) => entry.hubId),
    ...models.bookings.map((booking) => booking.hubId),
    ...models.dispatches.map((leg) => leg.hubId),
  ]);
  return hubs
    .filter((hub) => hub.isPilot || used.has(hub.id))
    .map(({ id, name }) => ({ id, name }));
}
